/* eslint-disable class-methods-use-this */
import {
  Extension,
  accessResponse,
  basicResponse,
  command,
  durtApiResponseType,
  extensionResponseType,
  infoResponse,
} from 'client/services/durtConstants';
import { digest, makePkceCode } from 'client/util/pkceGenerator';
import { sendMessageToChromeExtension, sendMessageToSafariExtension } from 'client/util/message';
import Util from 'client/util/Util';
import api from 'client/api';

import type { DurtAccess } from 'client/types/Auth';

const { user: userApi } = api;

export const extensions: {[name: string]: Extension} = {
  chrome: {
    id: 'jdoahkhfkeipblhbhppmcbdgapeoaopa',
    send: sendMessageToChromeExtension,
  },
  safari: {
    id: 'com.jumpcloud.JumpCloud-Protect.safari-extension (N985MXSH85)',
    send: sendMessageToSafariExtension,
  },
};

export class DurtService {
  private extension: Extension | undefined;

  async getInfo():Promise<infoResponse> {
    const info = await this.sendMessage<infoResponse>({ cmd: command.info });

    const validResults = [
      durtApiResponseType.success,
      durtApiResponseType.userNotRegisteredError,
    ];

    if (validResults.includes(info.result)) {
      return info;
    }
    throw new Error(info.result);
  }

  async signin(accessData: accessResponse): Promise<void> {
    if (accessData.result === durtApiResponseType.success) {
      await userApi.loginDURT({
        sessionToken: accessData.session,
        sessionTokenCode: accessData.pkceKey,
      });
    }
    // @TODO: what do we do here?
    // accessData.result === durtApiResponseType.userNotRegisteredError
  }

  // eslint-disable-next-line max-len
  async getAccess(userVerificationRequired = false, stateId = ''): Promise<accessResponse> {
    try {
      const pkceKey = makePkceCode();
      const pkceChallenge = await digest(pkceKey);
      const accessArgs:DurtAccess = {
        cmd: command.access,
        pkce: pkceChallenge,
      };

      if (stateId?.length) {
        accessArgs.state = stateId;
        accessArgs.uv = { required: userVerificationRequired };
      }

      const access = await this.sendMessage<accessResponse>(accessArgs);

      const validResults = [
        durtApiResponseType.success,
        durtApiResponseType.userNotRegisteredError,
      ];

      if (validResults.includes(access.result)) {
        return {
          ...access,
          pkceKey,
        };
      }

      throw new Error(access.result);
    } catch {
      throw new Error(durtApiResponseType.unexpecedError);
    }
  }

  async register(idToken:string): Promise<void> {
    const registerResponse = await this.sendMessage<basicResponse>({
      cmd: command.register,
      token: idToken,
    });

    if (registerResponse.result !== durtApiResponseType.success) {
      throw new Error(registerResponse.result);
    }
  }

  async hasExtension(): Promise<boolean> {
    try {
      if (!this.extension) {
        this.extension = await this.getExtension();
      }
      return true;
    } catch (err) {
      return false;
    }
  }

  private async sendMessage<T>(payload: any) {
    if (!this.extension) {
      this.extension = await this.getExtension();
    }

    return this.extension.send<T>(this.extension.id, payload);
  }

  private async getExtension() {
    if (await this.hasChomeExtension()) {
      return extensions.chrome;
    }
    if (await this.hasSafariExtension()) {
      return extensions.safari;
    }

    throw new Error('durt extension error: not found');
  }

  private async hasChomeExtension() {
    try {
      const response = await extensions.chrome.send<basicResponse>(extensions.chrome.id, { cmd: 'be_version' });
      return response.result === extensionResponseType.success;
    } catch (error) {
      return false;
    }
  }

  // TODO: add 'be_version' to safari so extension checks can be unified
  private async hasSafariExtension() {
    if (navigator.vendor !== 'Apple Computer, Inc.') return false;
    try {
      const response = await Promise.race([
        extensions.safari.send<basicResponse>(
          extensions.safari.id,
          { cmd: command.info },
        ),
        Util.delay(200), // timeout the safari extension check
      ]);
      return response.result === extensionResponseType.success;
    } catch (error) {
      return false;
    }
  }
}

// DurtService Singleton
export default new DurtService();
